import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text,
} from '@fluentui/react-components';
import { Dismiss20Regular, Info20Regular } from '@fluentui/react-icons';
import type { DisclaimerPreviewDialogProps } from '../../types/customDisclaimers';
import { formatDisclaimerPreviewTitle } from '../../utils/customDisclaimerUtils';
import { useStyles } from './DisclaimerPreviewDialog.style';

export const DisclaimerPreviewDialog: React.FC<DisclaimerPreviewDialogProps> = ({
  open,
  onClose,
  organisationName,
  text,
}) => {
  const styles = useStyles();
  const [portalMountNode, setPortalMountNode] = useState<HTMLDivElement | null>(null);

  return (
    <>
      <div className={styles.portalMount} ref={setPortalMountNode} data-testid="disclaimer-preview-portal-mount" />
      {portalMountNode && (
        <Dialog open={open} onOpenChange={(_event, data) => !data.open && onClose()}>
          <DialogSurface
            mountNode={portalMountNode}
            backdrop={{ appearance: 'transparent' }}
            className={styles.surface}
            data-testid="disclaimer-preview-dialog"
          >
            <DialogBody className={styles.body}>
              <div className={styles.header}>
                <DialogTitle className={styles.title}>
                  Disclaimer preview
                </DialogTitle>
                <Button
                  className={styles.dismissButton}
                  appearance="subtle"
                  size="small"
                  aria-label="Close preview"
                  icon={<Dismiss20Regular />}
                  onClick={onClose}
                  data-testid="disclaimer-preview-dismiss-button"
                />
              </div>
              <DialogContent className={styles.content}>
                <Text className={styles.description}>This is what recipients see in their chat conversations.</Text>
                <div
                  className={styles.disclaimer}
                  aria-label={`Preview delivered by ${organisationName}`}
                  data-testid="disclaimer-preview-conversation"
                >
                  <Text className={styles.disclaimerHeading} data-testid="disclaimer-preview-heading">
                    {formatDisclaimerPreviewTitle(organisationName)}
                  </Text>
                  <Text className={styles.disclaimerText} data-testid="disclaimer-preview-text">
                    {text}
                  </Text>
                </div>
                <div className={styles.notice}>
                  <Info20Regular />
                  <Text>Recipients may see this differently depending on their Microsoft Teams settings.</Text>
                </div>
              </DialogContent>
              <DialogActions className={styles.footer}>
                <Button className={styles.closeButton} appearance="secondary" size="small" onClick={onClose}>
                  Close
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
};